/**
 * Logística → Bóvedas (`/logistica/bovedas`).
 *
 * Puerto de `Logistics::vaults` + `views/vaults/index.php` (que se alimentaba de
 * `API/Boveda`) y de sus acciones `Boveda::editar_boveda` y `Boveda::eliminar_boveda`:
 * la tabla de ingresos a bóveda con sus filtros (fechas, usuario y monto mínimo),
 * la edición del monto, la fecha y la observación, y la inhabilitación del registro.
 *
 * Las dos acciones sólo se ofrecen en los turnos verificados y con su permiso
 * (`liquidaciones.bovedas.editar` / `.eliminar`), igual que el panel anterior; el
 * el resto de filas muestran el candado con el motivo. El **vale del depósito** se
 * genera y se imprime desde el propio panel (PDF de la API).
 */

import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Filter,
  Lock,
  Pencil,
  Printer,
  RotateCcw,
  ShieldAlert,
  Vault,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { useBovedas, useEditarBoveda, useEliminarBoveda } from '../bovedas.api';
import type { Boveda, BovedaEditable, BovedasFiltros } from '../bovedas.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Diálogo para editar el monto, la fecha y la observación de una bóveda. */
function EditarBovedaDialogo({
  boveda,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  boveda: Boveda;
  guardando: boolean;
  error: string | null;
  onGuardar: (datos: BovedaEditable) => void;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState<BovedaEditable>({
    total: String(boveda.total),
    fecha: (boveda.fecha ?? '').slice(0, 10),
    observacion: boveda.observacion ?? '',
  });

  const totalInvalido = datos.total === '' || Number(datos.total) < 0;
  const puedeGuardar =
    !totalInvalido && datos.fecha !== '' && datos.observacion.trim() !== '' && !guardando;

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="md"
      title="Editar bóveda"
      description={`Registro #${boveda.id} · ${boveda.usuario || 'usuario no informado'}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cerrar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() => onGuardar(datos)}
          >
            Guardar cambios
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="info">
          Al cambiar el monto se ajustan los acumulados del turno: el dinero pasa de la bóveda al
          efectivo del turno, como en el panel anterior.
        </Alert>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <Input label="Usuario" value={boveda.usuario} readOnly />

        <Input
          label="Monto"
          type="number"
          step="0.01"
          min={0}
          value={datos.total}
          error={totalInvalido ? 'El monto no puede estar vacío ni ser negativo.' : undefined}
          onChange={(evento) => setDatos({ ...datos, total: evento.target.value })}
        />

        <Input
          label="Fecha"
          type="date"
          value={datos.fecha}
          onChange={(evento) => setDatos({ ...datos, fecha: evento.target.value })}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="boveda-observacion" className="text-sm font-medium text-ink-700">
            Detalle
          </label>
          <textarea
            id="boveda-observacion"
            rows={4}
            maxLength={500}
            className={cn(CONTROL, 'h-auto py-2')}
            value={datos.observacion}
            onChange={(evento) => setDatos({ ...datos, observacion: evento.target.value })}
          />
          <p className="text-xs text-ink-400">{datos.observacion.length} de 500 caracteres</p>
        </div>
      </div>
    </Dialog>
  );
}

export function BovedasPage() {
  useDocumentTitle('Logística · Bóvedas');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [usuario, setUsuario] = useState('');
  const [montoMin, setMontoMin] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<BovedasFiltros>({
    orden: 'id',
    direccion: 'DESC',
    perPage: POR_PAGINA,
    page: 1,
  });
  const [editando, setEditando] = useState<Boveda | null>(null);
  const [aEliminar, setAEliminar] = useState<Boveda | null>(null);
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const bovedas = useBovedas(filtros);
  const guardar = useEditarBoveda();
  const eliminar = useEliminarBoveda();

  const sinPermiso = bovedas.error instanceof ApiError && bovedas.error.status === 403;
  const lista = bovedas.data?.bovedas ?? [];
  const total = bovedas.data?.total ?? 0;
  const pagina = bovedas.data?.page ?? 1;
  const paginas = bovedas.data?.paginas ?? 1;
  const permisos = bovedas.data?.permisos;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica los filtros y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      usuario: usuario.trim() || undefined,
      montoMin: montoMin.trim() || undefined,
      search: busqueda.trim() || undefined,
      orden: 'id',
      direccion: 'DESC',
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  /** Abre el vale del depósito (lo genera la propia API). */
  async function imprimirVale(boveda: Boveda) {
    setErrorImpresion(null);
    setImprimiendo(boveda.id);

    try {
      await abrirPdfAutenticado(`/api/logistica/bovedas/${boveda.id}/voucher`);
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar el vale.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Vault className="size-6 text-brand-500" aria-hidden="true" />
          Bóvedas
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Ingresos de efectivo a bóveda de cada turno, con la edición del monto y la inhabilitación
          del registro.
        </p>
      </div>

      {bovedas.data ? <Badge variant="outline">{formatNumber(total)} registros</Badge> : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            {mensajeDe(bovedas.error, 'Esta pantalla es del grupo logistics.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        La <strong>edición</strong> y la <strong>inhabilitación</strong> ya están disponibles en los
        turnos verificados y con su permiso (ajustan los acumulados del turno, como el panel
        anterior). El <strong>vale del depósito</strong> ya lo genera el propio panel; el envío a la
        impresora térmica (QZ Tray) se portará con el circuito de impresión.
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="bv-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="bv-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="bv-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="bv-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="bv-usuario" className="text-sm font-medium text-ink-700">
                Usuario
              </label>
              <input
                id="bv-usuario"
                className={cn(CONTROL, 'w-56')}
                placeholder="Nombre del usuario"
                value={usuario}
                onChange={(evento) => setUsuario(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="bv-monto" className="text-sm font-medium text-ink-700">
                Monto mínimo
              </label>
              <input
                id="bv-monto"
                type="number"
                step="0.01"
                min={0}
                className={cn(CONTROL, 'w-32')}
                placeholder="0.00"
                value={montoMin}
                onChange={(evento) => setMontoMin(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="bv-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="bv-buscar"
                className={cn(CONTROL, 'w-48')}
                placeholder="Detalle, usuario, monto…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                setDesde('');
                setHasta('');
                setUsuario('');
                setMontoMin('');
                setBusqueda('');
                setFiltros({ orden: 'id', direccion: 'DESC', perPage: POR_PAGINA, page: 1 });
              }}
            >
              Limpiar
            </Button>
          </div>

          {guardar.error || eliminar.error ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {mensajeDe(
                guardar.error ?? eliminar.error,
                'La API no respondió.',
              )}
            </Alert>
          ) : null}

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar el vale">
              {errorImpresion}
            </Alert>
          ) : null}

          {bovedas.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : bovedas.error ? (
            <Alert variant="error" title="No se pudieron leer las bóvedas">
              {mensajeDe(bovedas.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay ingresos a bóveda con esos filtros.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">#</th>
                    <th className="py-2">Usuario</th>
                    <th className="py-2 text-right">Cantidad</th>
                    <th className="py-2">Detalle</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Fecha creación</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((boveda) => (
                    <tr key={boveda.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3 text-ink-500">{boveda.id}</td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">
                          {boveda.usuario || '—'}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {boveda.turno_id ? `Turno #${boveda.turno_id}` : 'Sin turno'}
                          {boveda.procesada ? ' · procesada' : ''}
                        </span>
                      </td>
                      <td className="py-3 text-right font-semibold text-ink-900">
                        {formatCurrency(boveda.total)}
                      </td>
                      <td className="max-w-[240px] py-3 text-ink-600">
                        {boveda.observacion || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3 text-ink-700">{boveda.fecha ?? '—'}</td>
                      <td className="py-3 text-ink-700">{boveda.creado ?? '—'}</td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Imprimir el vale del depósito"
                            aria-label={`Imprimir el vale del depósito ${boveda.id}`}
                            loading={imprimiendo === boveda.id}
                            onClick={() => void imprimirVale(boveda)}
                          >
                            <Printer className="size-4 text-ink-600" aria-hidden="true" />
                          </Button>

                          {boveda.puede_editar ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar bóveda"
                              aria-label={`Editar la bóveda ${boveda.id}`}
                              onClick={() => {
                                guardar.reset();
                                setEditando(boveda);
                              }}
                            >
                              <Pencil className="size-4 text-amber-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title={
                                permisos?.editar
                                  ? 'Editar · sólo en turnos verificados'
                                  : 'Editar · falta el permiso liquidaciones.bovedas.editar'
                              }
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}

                          {boveda.puede_eliminar ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Inhabilitar bóveda"
                              aria-label={`Inhabilitar la bóveda ${boveda.id}`}
                              onClick={() => {
                                eliminar.reset();
                                setAEliminar(boveda);
                              }}
                            >
                              <Ban className="size-4 text-red-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title={
                                permisos?.eliminar
                                  ? 'Inhabilitar · sólo en turnos verificados'
                                  : 'Inhabilitar · falta el permiso liquidaciones.bovedas.eliminar'
                              }
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} registros · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {editando ? (
        <EditarBovedaDialogo
          boveda={editando}
          guardando={guardar.isPending}
          error={guardar.error ? mensajeDe(guardar.error, 'No se pudo guardar la bóveda.') : null}
          onGuardar={(datos) =>
            guardar.mutate(
              { id: editando.id, boveda: datos },
              { onSuccess: () => setEditando(null) },
            )
          }
          onCerrar={() => {
            guardar.reset();
            setEditando(null);
          }}
        />
      ) : null}

      {aEliminar ? (
        <ConfirmDialog
          open
          onClose={() => setAEliminar(null)}
          onConfirm={() => eliminar.mutate(aEliminar.id, { onSuccess: () => setAEliminar(null) })}
          title="¿Inhabilitar la bóveda?"
          description="El ingreso dejará de contar en el listado y su monto volverá al efectivo del turno, como en el panel anterior."
          confirmLabel="Sí, inhabilitar"
          loading={eliminar.isPending}
        />
      ) : null}
    </div>
  );
}
